// Cloud storage service for migrating from localStorage to backend API

import { apiService, Conversation, Message } from './api';
import { ConversationEntry, ConversationExchange } from '../types';

class CloudStorageService {
  private migrationInProgress = false;

  // Migrate existing localStorage data to cloud storage
  async migrateLocalStorageData(): Promise<void> {
    if (this.migrationInProgress) {
      return;
    }

    this.migrationInProgress = true;

    try {
      // Get existing conversations from localStorage
      const savedConversations = localStorage.getItem('soap-refiner-conversations');
      if (!savedConversations) {
        return;
      }

      const localConversations: ConversationEntry[] = JSON.parse(savedConversations);
      console.log(`Migrating ${localConversations.length} conversations to cloud storage...`);

      for (const localConv of localConversations) {
        try {
          // Create conversation in cloud storage
          const { conversation } = await apiService.createConversation(
            localConv.title || `Migrated Conversation ${new Date(localConv.startedAt).toLocaleDateString()}`,
            localConv.conversationType || 'mixed'
          );

          // Migrate all exchanges as messages
          for (const exchange of localConv.exchanges.reverse()) { // Reverse to maintain chronological order
            try {
              // Create user message
              await apiService.createMessage({
                conversation_id: conversation.id,
                role: 'user',
                original_text: exchange.originalText,
                anonymized_text: exchange.anonymizedText,
                analysis: '', // User messages don't have analysis
                message_type: exchange.messageType || 'chat',
                metadata: {
                  replacements: exchange.replacements || [],
                  timestamp: exchange.timestamp
                }
              });

              // Create assistant response if analysis exists
              if (exchange.analysis) {
                await apiService.createMessage({
                  conversation_id: conversation.id,
                  role: 'assistant',
                  original_text: '',
                  anonymized_text: '',
                  analysis: exchange.analysis,
                  message_type: exchange.messageType || 'chat',
                  metadata: {
                    timestamp: exchange.timestamp
                  }
                });
              }
            } catch (error) {
              console.error(`Failed to migrate exchange ${exchange.id}:`, error);
            }
          }

          console.log(`Migrated conversation: ${conversation.id}`);
        } catch (error) {
          console.error(`Failed to migrate conversation ${localConv.id}:`, error);
        }
      }

      // Clear localStorage after successful migration
      localStorage.removeItem('soap-refiner-conversations');
      console.log('Migration completed and localStorage cleared');
    } catch (error) {
      console.error('Migration failed:', error);
      throw error;
    } finally {
      this.migrationInProgress = false;
    }
  }

  // Load conversations from cloud storage
  async loadConversations(): Promise<ConversationEntry[]> {
    try {
      const { conversations } = await apiService.getConversations();
      
      const conversationEntries: ConversationEntry[] = [];

      for (const conv of conversations) {
        try {
          const { messages } = await apiService.getMessages(conv.id);
          
          // Convert messages back to exchanges format
          const exchanges: ConversationExchange[] = [];
          
          // Group messages by timestamp to create exchanges
          const messagesByTimestamp = new Map<string, Message[]>();
          
          for (const message of messages) {
            const timestampKey = message.metadata?.timestamp || message.timestamp;
            const key = new Date(timestampKey).toISOString();
            
            if (!messagesByTimestamp.has(key)) {
              messagesByTimestamp.set(key, []);
            }
            messagesByTimestamp.get(key)!.push(message);
          }

          // Convert grouped messages to exchanges
          for (const [timestamp, msgs] of Array.from(messagesByTimestamp.entries())) {
            const userMessage = msgs.find(m => m.role === 'user');
            const assistantMessage = msgs.find(m => m.role === 'assistant');
            
            if (userMessage) {
              const exchange: ConversationExchange = {
                id: userMessage.id,
                timestamp: new Date(timestamp),
                originalText: userMessage.original_text || userMessage.anonymized_text || '',
                anonymizedText: userMessage.anonymized_text || userMessage.original_text || '',
                analysis: assistantMessage?.analysis || '',
                replacements: userMessage.metadata?.replacements || [],
                messageType: userMessage.message_type as 'soap_note' | 'chat' | 'system'
              };
              exchanges.push(exchange);
            }
          }

          // Sort exchanges by timestamp (newest first to match current UI)
          exchanges.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

          const conversationEntry: ConversationEntry = {
            id: conv.id,
            startedAt: new Date(conv.created_at),
            lastUpdated: new Date(conv.updated_at),
            exchanges,
            title: conv.title,
            conversationType: conv.conversation_type
          };

          conversationEntries.push(conversationEntry);
        } catch (error) {
          console.error(`Failed to load messages for conversation ${conv.id}:`, error);
        }
      }

      // Sort conversations by last updated (newest first)
      conversationEntries.sort((a, b) => b.lastUpdated.getTime() - a.lastUpdated.getTime());

      return conversationEntries;
    } catch (error) {
      console.error('Failed to load conversations from cloud:', error);
      throw error;
    }
  }

  // Save a conversation to cloud storage
  async saveConversation(conversation: ConversationEntry): Promise<void> {
    try {
      // Check if conversation exists
      let cloudConversation: Conversation;
      
      try {
        // Try to get existing conversation
        const { conversations } = await apiService.getConversations();
        const existing = conversations.find(c => c.id === conversation.id);
        
        if (existing) {
          cloudConversation = existing;
        } else {
          // Create new conversation
          const { conversation: newConv } = await apiService.createConversation(
            conversation.title,
            conversation.conversationType
          );
          cloudConversation = newConv;
        }
      } catch (error) {
        // Create new conversation if not found
        const { conversation: newConv } = await apiService.createConversation(
          conversation.title,
          conversation.conversationType
        );
        cloudConversation = newConv;
      }

      // Get existing messages to avoid duplicates
      const { messages: existingMessages } = await apiService.getMessages(cloudConversation.id);
      const existingMessageIds = new Set(existingMessages.map(m => m.id));

      // Save new exchanges as messages
      for (const exchange of conversation.exchanges) {
        if (!existingMessageIds.has(exchange.id)) {
          try {
            // Create user message
            await apiService.createMessage({
              conversation_id: cloudConversation.id,
              role: 'user',
              original_text: exchange.originalText,
              anonymized_text: exchange.anonymizedText,
              analysis: '',
              message_type: exchange.messageType || 'chat',
              metadata: {
                replacements: exchange.replacements || [],
                timestamp: exchange.timestamp.toISOString()
              }
            });

            // Create assistant response if analysis exists
            if (exchange.analysis.trim()) {
              await apiService.createMessage({
                conversation_id: cloudConversation.id,
                role: 'assistant',
                original_text: '',
                anonymized_text: '',
                analysis: exchange.analysis,
                message_type: exchange.messageType || 'chat',
                metadata: {
                  timestamp: exchange.timestamp.toISOString()
                }
              });
            }
          } catch (error) {
            console.error(`Failed to save exchange ${exchange.id}:`, error);
          }
        }
      }

      // Update conversation metadata
      await apiService.updateConversation(cloudConversation.id, {
        title: conversation.title,
        conversation_type: conversation.conversationType
      });
    } catch (error) {
      console.error('Failed to save conversation to cloud:', error);
      throw error;
    }
  }

  // Delete a conversation from cloud storage
  async deleteConversation(conversationId: string): Promise<void> {
    try {
      await apiService.deleteConversation(conversationId);
    } catch (error) {
      console.error(`Failed to delete conversation ${conversationId}:`, error);
      throw error;
    }
  }

  // Search conversations in cloud storage
  async searchConversations(query: string): Promise<ConversationEntry[]> {
    try {
      const { conversations } = await apiService.getConversations(50, 0, query);
      
      const conversationEntries: ConversationEntry[] = [];

      for (const conv of conversations) {
        try {
          const { messages } = await apiService.getMessages(conv.id, 10); // Limit messages for search results
          
          const exchanges: ConversationExchange[] = messages
            .filter(m => m.role === 'user')
            .map(m => ({
              id: m.id,
              timestamp: new Date(m.timestamp),
              originalText: m.original_text || m.anonymized_text || '',
              anonymizedText: m.anonymized_text || m.original_text || '',
              analysis: '',
              replacements: m.metadata?.replacements || [],
              messageType: m.message_type as 'soap_note' | 'chat' | 'system'
            }));

          const conversationEntry: ConversationEntry = {
            id: conv.id,
            startedAt: new Date(conv.created_at),
            lastUpdated: new Date(conv.updated_at),
            exchanges,
            title: conv.title,
            conversationType: conv.conversation_type
          };

          conversationEntries.push(conversationEntry);
        } catch (error) {
          console.error(`Failed to load messages for search result ${conv.id}:`, error);
        }
      }

      return conversationEntries;
    } catch (error) {
      console.error('Failed to search conversations:', error);
      throw error;
    }
  }
}

export const cloudStorageService = new CloudStorageService();